import { useCallback, useEffect, useState } from "react";
import { Layout } from "./dashboard/components/Layout";
import { Overview } from "./dashboard/pages/Overview";
import { Agents } from "./dashboard/pages/Agents";
import { Prospects } from "./dashboard/pages/Prospects";
import { Settings } from "./dashboard/pages/Settings";
import { Landing } from "./dashboard/pages/Landing";
import { AuditPage } from "./dashboard/pages/Audit";
import { CRM } from "./dashboard/pages/CRM";
import { Decisions } from "./dashboard/pages/Decisions";
import { Posts } from "./dashboard/pages/Posts";
import { Metrics } from "./dashboard/pages/Metrics";
import { Audits } from "./dashboard/pages/Audits";
import { AuditDetail } from "./dashboard/pages/AuditDetail";
import { LinkedinHub } from "./dashboard/pages/LinkedinHub";
import { Admin } from "./dashboard/pages/Admin";
import { Activity } from "./dashboard/pages/Activity";
import { Errors } from "./dashboard/pages/Errors";
import { Templates } from "./dashboard/pages/Templates";
import { EngagementProvider } from "./dashboard/hooks/useEngagement";
import { AgentSocketProvider } from "./dashboard/hooks/useAgentSocketContext";
import { CommandPalette } from "./dashboard/components/CommandPalette";

export type Page =
  | "overview"
  | "agents"
  | "prospects"
  | "crm"
  | "decisions"
  | "posts"
  | "metrics"
  | "audits"
  | "audit-detail"
  | "linkedin"
  | "admin"
  | "activity"
  | "errors"
  | "templates"
  | "settings"
  | "landing"
  | "audit";

function getInitialPage(): Page {
  const path = window.location.pathname;
  if (path === "/landing" || path.startsWith("/landing")) return "landing";
  // Order matters: /audits must match before /audit to avoid the
  // /audit prefix swallowing the dashboard list route.
  if (path === "/admin" || path.startsWith("/admin")) return "admin";
  if (path === "/activity" || path.startsWith("/activity")) return "activity";
  if (path === "/errors" || path.startsWith("/errors")) return "errors";
  if (path === "/templates" || path.startsWith("/templates")) return "templates";
  if (path === "/linkedin" || path.startsWith("/linkedin")) return "linkedin";
  // /audits/<id> (a segment after /audits/) is the per-audit drill-down;
  // /audits (no trailing segment) is the list. Check the detail form first.
  if (/^\/audits\/.+/.test(path)) return "audit-detail";
  if (path === "/audits" || path.startsWith("/audits")) return "audits";
  if (path === "/audit" || path.startsWith("/audit")) return "audit";
  if (path === "/crm" || path.startsWith("/crm")) return "crm";
  if (path === "/decisions" || path.startsWith("/decisions")) return "decisions";
  if (path === "/posts" || path.startsWith("/posts")) return "posts";
  if (path === "/metrics" || path.startsWith("/metrics")) return "metrics";
  if (path === "/settings" || path.startsWith("/settings")) return "settings";
  if (path === "/agents" || path.startsWith("/agents")) return "agents";
  if (path === "/prospects" || path.startsWith("/prospects")) return "prospects";
  return "overview";
}

function pageToPath(page: Page): string {
  if (page === "overview") return "/";
  // The detail page is only ever reached via a deep link (/audits/<id>),
  // never via the sidebar — keep whatever path is already in the bar so
  // we don't synthesise a bogus "/audit-detail".
  if (page === "audit-detail") return window.location.pathname;
  return `/${page}`;
}

export function App() {
  const [page, setPageState] = useState<Page>(getInitialPage);

  // Sync sidebar clicks → URL bar so the path always reflects the
  // current page (and can be deep-linked, bookmarked, shared).
  const setPage = useCallback((next: Page) => {
    setPageState(next);
    const target = pageToPath(next);
    if (window.location.pathname !== target) {
      // Preserve the query string (engagement filter etc.).
      const qs = window.location.search;
      window.history.pushState({}, "", target + qs);
    }
  }, []);

  // Browser back/forward → react to the path change.
  useEffect(() => {
    const onPop = () => setPageState(getInitialPage());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Full-screen public pages (no dashboard sidebar, no engagement picker).
  if (page === "landing") {
    return <Landing />;
  }
  if (page === "audit") {
    return <AuditPage />;
  }

  return (
    <AgentSocketProvider>
      <EngagementProvider>
        <Layout currentPage={page === "audit-detail" ? "audits" : page} onNavigate={setPage}>
          {page === "overview" && <Overview />}
          {page === "agents" && <Agents />}
          {page === "prospects" && <Prospects />}
          {page === "crm" && <CRM />}
          {page === "decisions" && <Decisions />}
          {page === "posts" && <Posts />}
          {page === "metrics" && <Metrics />}
          {page === "audits" && <Audits />}
          {page === "audit-detail" && <AuditDetail />}
          {page === "linkedin" && <LinkedinHub />}
          {page === "admin" && <Admin />}
          {page === "activity" && <Activity />}
          {page === "errors" && <Errors />}
          {page === "templates" && <Templates />}
          {page === "settings" && <Settings />}
        </Layout>
        <CommandPalette />
      </EngagementProvider>
    </AgentSocketProvider>
  );
}
